import React, { forwardRef, type HTMLAttributes } from "react";

import { CrossIcon } from "@eisox/icons";
import * as RadixDialog from "@radix-ui/react-dialog";

import { Circle } from "../Circle";
import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/
const ROOT_NAME = "Dialog";
type RootProps = RadixDialog.DialogProps;
const Root = RadixDialog.Root;
Root.displayName = ROOT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/
const TRIGGER_NAME = "DialogTrigger";
type TriggerProps = RadixDialog.DialogTriggerProps;
const Trigger = RadixDialog.Trigger;
Trigger.displayName = TRIGGER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Close
 * -----------------------------------------------------------------------------------------------*/
const CLOSE_NAME = "DialogClose";
type CloseProps = RadixDialog.DialogCloseProps;
const Close = RadixDialog.Close;
Close.displayName = CLOSE_NAME;

/* -------------------------------------------------------------------------------------------------
 * Footer
 * -----------------------------------------------------------------------------------------------*/
const FOOTER_NAME = "DialogFooter";
type FooterProps = HTMLAttributes<HTMLDivElement>;
const Footer: React.FC<FooterProps> = ({ className, children, ...props }) => (
  <div {...props} className={cn("gap-6 flex flex-row items-center justify-center", className)}>
    {children}
  </div>
);

Footer.displayName = FOOTER_NAME;

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/
const CONTENT_NAME = "DialogContent";
interface ContentProps extends RadixDialog.DialogContentProps {
  title?: string;
  icon?: React.ReactElement;
}
const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ title, icon, children, className, ...props }, forwardedRef) => (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="overlay" />
      <RadixDialog.Content
        {...props}
        ref={forwardedRef}
        className={cn(
          `gap-5 p-2.5 md:min-w-[400px] md:gap-12 md:p-10 content flex min-w-[min(400px,calc(100vw-40px))] flex-col items-center`,
          className,
        )}
      >
        <RadixDialog.Close className={"right-5 md:right-10 absolute max-w-none"}>
          <CrossIcon className="h-[15px] w-[15px] cursor-pointer stroke-primary-dark-gray" />
        </RadixDialog.Close>
        {icon && (
          <Circle.Root className="md:h-[70px] md:w-[70px] h-[50px] w-[50px]" backgroundColor="gray" color="darkBlue">
            {icon}
          </Circle.Root>
        )}
        {title && (
          <RadixDialog.Title className="mb-0 font-semibold md:text-2xl text-center text-xl whitespace-pre-line">
            {title}
          </RadixDialog.Title>
        )}
        {children}
      </RadixDialog.Content>
    </RadixDialog.Portal>
  ),
);

Content.displayName = CONTENT_NAME;

/* -------------------------------------------------------------------------------------------------
 * Title
 * -----------------------------------------------------------------------------------------------*/
const TITLE_NAME = "DialogTitle";
type TitleProps = RadixDialog.DialogTitleProps;
/**
 * Radix requires every `Content` to be labelled by a title. `Content` renders one from its `title`
 * prop; this export is for dialogs that style their own header — including visually hidden ones.
 */
const Title = RadixDialog.Title;
Title.displayName = TITLE_NAME;

/* -------------------------------------------------------------------------------------------------
 * Description
 * -----------------------------------------------------------------------------------------------*/
const DESCRIPTION_NAME = "DialogDescription";
type DescriptionProps = RadixDialog.DialogDescriptionProps;
const Description = RadixDialog.Description;
Description.displayName = DESCRIPTION_NAME;

export { Root, Trigger, Content, Close, Title, Description, Footer };
export type { RootProps, TriggerProps, ContentProps, CloseProps, TitleProps, DescriptionProps, FooterProps };
